import React, { useRef } from 'react';
import type { CanvasComponent as CanvasComponentType } from '../../../types/project';
import CanvasComponent from './CanvasComponent';
import styles from './Canvas.module.css';
import type { ComponentType } from '../../../types/project';

// ── Props ─────────────────────────────────────────────────────────────────────

type CanvasProps = {
  components: CanvasComponentType[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, dx: number, dy: number) => void;
  onRemove: (id: string) => void;
  onDrop: (type: ComponentType, x: number, y: number) => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

function Canvas({
  components,
  selectedId,
  onSelect,
  onMove,
  onRemove,
  onDrop,
}: CanvasProps): React.ReactElement {
  const canvasRef = useRef<HTMLDivElement>(null);

  // ── Drag-over / drop (from palette drag) ──────────────────────────────────
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('text/plain') as ComponentType;
    if (!type) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    onDrop(type, x, y);
  };

  // ── Click on canvas background deselects ──────────────────────────────────
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === canvasRef.current) {
      onSelect(null);
    }
  };

  return (
    <div
      ref={canvasRef}
      className={styles.canvas}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleBackgroundClick}
    >
      {components.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>Canvas is empty</p>
          <p className={styles.emptyHint}>
            Click a component in the palette to add it, or drag it here.
          </p>
        </div>
      )}

      {components.map((c) => (
        <CanvasComponent
          key={c.id}
          component={c}
          selected={c.id === selectedId}
          onSelect={onSelect}
          onMove={onMove}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

export default Canvas;
